// «Умный СТО» (агент sto_intel): мастер нового отчёта и титул (Прил. Б), служебные листы, состав работы по
// табл. 1 СТО, структура с перетаскиванием разделов, перекрёстные ссылки («@», живые номера), библиотека
// источников по ГОСТ Р 7.0.100 (импорт BibTeX/RIS/списка), сноски/примечания/примеры, типографика на лету,
// живая проверка с подчёркиванием и исправлениями, полная проверка sto_check с группировкой по разделам СТО.
import type { RegistryApi, Ctx } from '../../core/registry';
import { NodeSelection } from '@tiptap/pm/state';
import { insertBlocks, focusInside } from '../../core/editor/insert';
import { runCheck } from '../../core/project';
import { STRUCT_KINDS, USE_NBSP } from '../../core/schema/constants';
import { appStore } from '../../core/store';
import { ctx } from '../../core/ctx';
import { gotoNode } from '../../core/docIndex';
import { MetaDialog } from './meta';
import { TitleSheet, TitlePreviewDialog } from './title';
import { FrontBar, ZadanieSheet, RemarksSheet, ReferatSheet, TocSheet, FrontWidgetsExtension } from './front';
import { SourcesPanel, SourceDialog, SourceImportDialog, CiteDialog, scheduleFormat } from './sources';
import { CiteView, XrefView, FootnoteView, StructHeadingView, XrefDialog, FootnoteDialog } from './refs';
import { StructurePanel, CheckPanel, PreExportDialog } from './panels';
import { WizardDialog, ProfileStart, ProfileDialog } from './wizard';
import { registerLint } from './lint';
import { isSheet } from './data';
import { TypographyExtension, applyTypography } from './typography';
import { ListCaseExtension } from '../../core/editor/listCase';
import { MentionExtension } from './mention';
import { InlineLintExtension, rescan, scanDoc, applyFix } from './inlineLint';
import { numberingStore } from '../../core/editor/numberingPlugin';
import { StoStatus } from './overlays';
import { DeleteGuardExtension } from './deleteGuard';
import { insertStructElement } from './structure';
import { loadProfile, prefs } from './profile';
import './sto.css';

// 3.5.1 СТО 2026: «Термины…» и «Перечень сокращений…» отдельно не делают - только «Определения, обозначения и сокращения»
const STRUCT_ORDER = ['intro', 'defs', 'normrefs', 'conclusions', 'conclusion', 'sources'];

function insertAppendix(c: Ctx) {
  const ed = c.editor;
  if (!ed) return;
  const end = ed.state.doc.content.size;          // приложения - в конце документа (4.16.1)
  ed.chain().focus().insertContentAt(end, { type: 'appendix', attrs: { status: 'обязательное' },
    content: [{ type: 'appendixTitle' }, { type: 'paragraph' }] }).run();
  focusInside(ed, end);
}

/** Примечание / пример: непустой абзац под курсором превращается в примечание, иначе - новый блок. */
function insertWrapped(c: Ctx, type: 'note' | 'example') {
  const ed = c.editor;
  if (!ed) return;
  const $f = ed.state.selection.$from;
  const para = $f.parent;
  const parentName = $f.depth > 1 ? $f.node($f.depth - 1).type.name : 'doc';
  if (para.type.name === 'paragraph' && para.content.size && (parentName === 'doc' || parentName === 'appendix')) {
    const from = $f.before($f.depth);
    const json = para.toJSON();
    const re = type === 'note' ? /^(Примечани[ея])(?:[\s ]*[–—:.-][\s ]*|[\s ]*$)/ : /^(Пример[ы]?)(?:[\s ]*[–—:.-][\s ]*|[\s ]*$)/;
    if (json.content?.[0]?.type === 'text') json.content[0].text = json.content[0].text.replace(re, '');
    ed.chain().focus().insertContentAt({ from, to: from + para.nodeSize }, { type, content: [json] }).run();
    return;
  }
  const pos = insertBlocks(ed, { type, content: [{ type: 'paragraph' }] });
  focusInside(ed, pos);
}

function openPanel(side: 'left' | 'right', id: string) {
  const p = appStore.getState().panels;
  if (p[side] !== id) appStore.getState().set({ panels: { ...p, [side]: id } });
}

export default function setup(r: RegistryApi) {
  // --- проверка
  r.commands.add({ id: 'sto.check', title: 'Проверить по СТО', description: 'Собрать DOCX и PDF в Word и проверить все требования СТО',
    icon: 'check', shortcut: 'F7', run: () => { openPanel('right', 'check'); return runCheck(true); },
    isEnabled: () => !appStore.getState().check.running });
  r.commands.add({ id: 'sto.checkFast', title: 'Быстрая проверка', description: 'Без Word: быстрее, но без PDF, оглавления и переносов таблиц',
    icon: 'refresh', run: () => { openPanel('right', 'check'); return runCheck(false); } });
  r.commands.add({ id: 'sto.preExport', title: 'Готовность к сдаче', description: 'Сводка по СТО перед экспортом: титул, ссылки, источники, состав, ошибки',
    icon: 'file-check', run: (c) => c.openDialog('sto.preExport', {}) });
  r.commands.add({ id: 'sto.meta', title: 'Данные работы', description: 'Тема, дисциплина, руководитель, студент, состав работы, раздел об ИИ',
    icon: 'title', run: (c, arg: any) => c.openDialog('sto.meta', arg || {}) });
  r.commands.add({ id: 'sto.newReport', title: 'Новый отчёт (мастер)', description: 'Вид работы, титул, состав по табл. 1 СТО', icon: 'new',
    run: (c) => c.openDialog('sto.wizard', { navigate: true }) });
  r.commands.add({ id: 'sto.profile', title: 'Профиль студента', description: 'Данные титула по умолчанию, руководители, дисциплины', icon: 'student',
    run: (c) => c.openDialog('sto.profile', {}) });
  r.commands.add({ id: 'sto.titlePreview', title: 'Титул: точный вид', description: 'Собрать титульный лист в Word и показать страницы',
    icon: 'eye', run: (c) => c.openDialog('sto.titlePreview', {}) });
  r.commands.add({ id: 'sto.gotoNode', title: 'Перейти к объекту', run: (c, id) => { if (c.editor && typeof id === 'string') gotoNode(c.editor, id); } });
  // --- типографика и живая проверка
  r.commands.add({ id: 'sto.typoToggle', title: 'Типографика при наборе', description: '«ёлочки», тире, диапазоны 1–4, ' + (USE_NBSP ? 'неразрывные пробелы' : '«№ 5», «50 %», «т. е.» через пробел') + ' (Ctrl+Z или Backspace - отменить замену)',
    icon: 'type', run: () => { prefs.typo = !prefs.typo; ctx.toast(prefs.typo ? 'Типографика при наборе включена' : 'Типографика при наборе выключена', 'info'); },
    isActive: () => prefs.typo });
  r.commands.add({ id: 'sto.typoApply', title: 'Типографика во всём документе', description: 'Кавычки, тире, диапазоны, пробелы - одним действием (Ctrl+Z - отменить)',
    icon: 'quote', run: (c) => { if (c.editor) c.toast(`Исправлено мест: ${applyTypography(c.editor)}`, 'ok'); } });
  r.commands.add({ id: 'sto.lintToggle', title: 'Подчёркивать нарушения', description: 'Волнистое подчёркивание нарушений СТО в тексте',
    icon: 'warn', run: (c) => { prefs.lintInline = !prefs.lintInline; if (c.editor) rescan(c.editor.view); }, isActive: () => prefs.lintInline });
  // --- структура
  for (const k of STRUCT_ORDER) {
    r.commands.add({ id: 'sto.struct.' + k, title: STRUCT_KINDS[k], description: 'Добавить на своё место по СТО (или перейти, если уже есть)',
      icon: 'bookmark', run: (c) => { if (c.editor) insertStructElement(c.editor, k); } });
  }
  r.commands.add({ id: 'sto.appendix', title: 'Приложение', description: 'Приложение с буквой, статусом и названием (в конце документа)', icon: 'attach', run: insertAppendix });
  r.commands.add({ id: 'insert.note', title: 'Примечание', description: '«Примечание – …» (4.10); абзац под курсором станет примечанием', icon: 'note',
    run: (c) => insertWrapped(c, 'note') });
  r.commands.add({ id: 'insert.example', title: 'Пример', description: '«Пример – …» (4.15)', icon: 'example', run: (c) => insertWrapped(c, 'example') });
  r.commands.add({ id: 'insert.pageBreak', title: 'Разрыв страницы', icon: 'pagebreak', shortcut: 'Mod-Enter',
    run: (c) => { if (c.editor) insertBlocks(c.editor, [{ type: 'pageBreak' }, { type: 'paragraph' }]); } });
  // --- ссылки и источники
  r.commands.add({ id: 'refs.cite', title: 'Ссылка на источник', description: 'Вставить [N] или [N, с. 5] - номер подставится сам (или «@» в тексте)', icon: 'cite',
    shortcut: 'Mod-Shift-c', run: (c) => c.openDialog('sto.cite', {}) });
  r.commands.add({ id: 'refs.editCite', title: 'Изменить ссылку на источник', icon: 'cite', run: (c) => {
    const sel = c.editor?.state.selection;
    if (sel instanceof NodeSelection && sel.node.type.name === 'cite') return c.openDialog('sto.cite', { items: sel.node.attrs.items, replacePos: sel.from });
    return c.openDialog('sto.cite', {});
  } });
  r.commands.add({ id: 'refs.xref', title: 'Перекрёстная ссылка', description: 'На рисунок, таблицу, формулу, листинг, приложение, раздел (или «@» в тексте)',
    icon: 'xref', shortcut: 'Mod-Shift-r', run: (c) => c.openDialog('sto.xref', {}) });
  r.commands.add({ id: 'refs.footnote', title: 'Сноска', description: 'Сноска внизу страницы (4.10.9), Ctrl+Alt+F', icon: 'footnote', shortcut: 'Mod-Alt-f', run: async (c) => {
    const ed = c.editor;
    if (!ed) return;
    // знак сноски - перед знаком препинания (4.10.9): «слово¹.», даже если курсор после точки
    const { from, empty } = ed.state.selection;
    const prev = empty ? ed.state.doc.textBetween(Math.max(0, from - 1), from, '', '') : '';
    const at = /[.,:;!?…]/.test(prev) ? from - 1 : from;
    const content = await c.openDialog<any[]>('sto.footnote', { content: [] });
    if (content && content.length) c.editor?.chain().focus().insertContentAt(at, { type: 'footnote', attrs: { content } }).run();
  } });
  r.commands.add({ id: 'refs.addSource', title: 'Новый источник', icon: 'plus', run: (c) => c.openDialog('sto.source', {}) });
  r.commands.add({ id: 'refs.importSources', title: 'Импорт источников', description: 'BibTeX, RIS или готовый список литературы', icon: 'import',
    run: (c) => c.openDialog('sto.sourceImport', {}) });
  r.commands.add({ id: 'refs.sourcesPanel', title: 'Библиотека источников', icon: 'library', run: () => openPanel('right', 'sources') });

  // --- лента
  r.ribbonGroups.add({ id: 'sto.check', tab: 'sto', title: 'Проверка', order: 10 });
  r.ribbonGroups.add({ id: 'sto.doc', tab: 'sto', title: 'Документ', order: 20 });
  r.ribbonGroups.add({ id: 'sto.struct', tab: 'sto', title: 'Структура', order: 30 });
  r.ribbonGroups.add({ id: 'sto.typo', tab: 'sto', title: 'Типографика', order: 40 });
  r.ribbonItems.add({ id: 'check', group: 'sto.check', type: 'large', command: 'sto.check' });
  r.ribbonItems.add({ id: 'check-fast', group: 'sto.check', type: 'large', command: 'sto.checkFast' });
  r.ribbonItems.add({ id: 'preexport', group: 'sto.check', type: 'large', command: 'sto.preExport', label: 'Готовность' });
  r.ribbonItems.add({ id: 'meta', group: 'sto.doc', type: 'large', command: 'sto.meta', label: 'Титул и данные' });
  r.ribbonItems.add({ id: 'title-exact', group: 'sto.doc', type: 'large', command: 'sto.titlePreview', label: 'Точный титул' });
  r.ribbonItems.add({ id: 'profile', group: 'sto.doc', type: 'button', command: 'sto.profile', label: 'Профиль' });
  r.ribbonItems.add({ id: 'struct', group: 'sto.struct', type: 'menu', label: 'Элемент', icon: 'bookmark',
    items: STRUCT_ORDER.map((k) => ({ id: 'struct-' + k, command: 'sto.struct.' + k })) });
  r.ribbonItems.add({ id: 'appendix', group: 'sto.struct', type: 'large', command: 'sto.appendix' });
  r.ribbonItems.add({ id: 'pagebreak', group: 'sto.struct', type: 'large', command: 'insert.pageBreak', label: 'Разрыв' });
  r.ribbonItems.add({ id: 'typo-toggle', group: 'sto.typo', type: 'toggle', command: 'sto.typoToggle', label: 'На лету' });
  r.ribbonItems.add({ id: 'typo-apply', group: 'sto.typo', type: 'button', command: 'sto.typoApply', label: 'Весь документ' });
  r.ribbonItems.add({ id: 'lint-toggle', group: 'sto.typo', type: 'toggle', command: 'sto.lintToggle', label: 'Подчёркивание' });

  r.ribbonGroups.add({ id: 'insert.text', tab: 'insert', title: 'Текст', order: 40 });
  r.ribbonItems.add({ id: 'note', group: 'insert.text', type: 'large', command: 'insert.note' });
  r.ribbonItems.add({ id: 'example', group: 'insert.text', type: 'large', command: 'insert.example' });
  r.ribbonItems.add({ id: 'ins-footnote', group: 'insert.text', type: 'large', command: 'refs.footnote' });
  r.ribbonItems.add({ id: 'ins-struct', group: 'insert.text', type: 'menu', label: 'Структура', icon: 'bookmark',
    items: [...STRUCT_ORDER.map((k) => ({ command: 'sto.struct.' + k })), { separator: true }, { command: 'sto.appendix' }, { command: 'insert.pageBreak' }] });

  r.ribbonGroups.add({ id: 'refs.sources', tab: 'refs', title: 'Источники', order: 10 });
  r.ribbonGroups.add({ id: 'refs.links', tab: 'refs', title: 'Ссылки', order: 20 });
  r.ribbonItems.add({ id: 'cite', group: 'refs.sources', type: 'large', command: 'refs.cite', label: 'Источник [N]' });
  r.ribbonItems.add({ id: 'add-source', group: 'refs.sources', type: 'large', command: 'refs.addSource' });
  r.ribbonItems.add({ id: 'import-sources', group: 'refs.sources', type: 'large', command: 'refs.importSources', label: 'Импорт' });
  r.ribbonItems.add({ id: 'sources-panel', group: 'refs.sources', type: 'large', command: 'refs.sourcesPanel', label: 'Библиотека' });
  r.ribbonItems.add({ id: 'xref', group: 'refs.links', type: 'large', command: 'refs.xref', label: 'Ссылка' });
  r.ribbonItems.add({ id: 'footnote', group: 'refs.links', type: 'large', command: 'refs.footnote' });

  // --- панели, диалоги, листы, отображения узлов, расширения
  r.panels.add({ id: 'structure', title: 'Структура', icon: 'list-tree', side: 'left', order: 10, component: StructurePanel });
  r.panels.add({ id: 'check', title: 'Проверка', icon: 'check', side: 'right', order: 10, component: CheckPanel, badgeTone: 'error',
    badge: (c) => { const res = c.store.getState().check.result; const live = c.store.getState().lint.filter((x) => x.sev === 'E').length; return (res ? res.errors : 0) + live || null; } });
  r.panels.add({ id: 'sources', title: 'Источники', icon: 'library', side: 'right', order: 20, component: SourcesPanel,
    badge: (c) => c.store.getState().sources.length || null });
  r.dialogs.add({ id: 'sto.meta', component: MetaDialog });
  r.dialogs.add({ id: 'sto.wizard', component: WizardDialog });
  r.dialogs.add({ id: 'project.create', component: WizardDialog });   // «Новый отчёт» на стартовом экране - мастер
  r.dialogs.add({ id: 'sto.profile', component: ProfileDialog });
  r.dialogs.add({ id: 'sto.titlePreview', component: TitlePreviewDialog });
  r.dialogs.add({ id: 'sto.preExport', component: PreExportDialog });
  r.dialogs.add({ id: 'sto.source', component: SourceDialog });
  r.dialogs.add({ id: 'sto.sourceImport', component: SourceImportDialog });
  r.dialogs.add({ id: 'sto.cite', component: CiteDialog });
  r.dialogs.add({ id: 'sto.xref', component: XrefDialog });
  r.dialogs.add({ id: 'sto.footnote', component: FootnoteDialog });
  // чистый лист (meta.type 'sheet') - только страницы текста: ни состава работы, ни титула, ни служебных листов
  const work = (c: Ctx) => !isSheet(c.store.getState().meta);
  r.sheets.add({ id: 'front', order: 5, component: FrontBar, visible: work });
  r.sheets.add({ id: 'title', order: 10, component: TitleSheet, visible: work });
  r.sheets.add({ id: 'zadanie', order: 20, component: ZadanieSheet, visible: (c) => work(c) && !!c.store.getState().meta?.zadanie.enabled });
  r.sheets.add({ id: 'remarks', order: 30, component: RemarksSheet, visible: (c) => work(c) && !!c.store.getState().meta?.remarks_page });
  r.sheets.add({ id: 'referat', order: 40, component: ReferatSheet,
    visible: (c) => work(c) && !!(c.store.getState().meta?.referat.enabled || c.store.getState().meta?.annotation.enabled) });
  r.sheets.add({ id: 'toc', order: 50, component: TocSheet, visible: work });
  r.nodeViews.add({ id: 'cite', component: CiteView, options: { as: 'span' } });
  r.nodeViews.add({ id: 'xref', component: XrefView, options: { as: 'span' } });
  r.nodeViews.add({ id: 'footnote', component: FootnoteView, options: { as: 'span' } });
  r.nodeViews.add({ id: 'structHeading', component: StructHeadingView });
  r.extensions.add({ id: 'sto.typography', create: () => TypographyExtension });
  r.extensions.add({ id: 'sto.listCase', create: () => ListCaseExtension });
  r.extensions.add({ id: 'sto.mention', create: () => MentionExtension });
  r.extensions.add({ id: 'sto.inlineLint', create: () => InlineLintExtension });
  r.extensions.add({ id: 'sto.deleteGuard', create: () => DeleteGuardExtension });
  r.extensions.add({ id: 'sto.frontWidgets', create: () => FrontWidgetsExtension });
  r.statusbar.add({ id: 'sto.status', side: 'left', order: 20, component: StoStatus });
  r.startSections.add({ id: 'sto.profile', order: 5, component: ProfileStart });

  // --- контекстное меню: исправления под курсором, ссылки, вид элемента, статус приложения
  r.contextMenu.add({ id: 'sto.refs', order: 50, items: (c, t) => {
    const out: any[] = [];
    if (c.editor && t.pos !== null) {
      const hits = scanDoc(c.editor.state.doc, numberingStore.get()).filter((x) => x.from <= t.pos! && t.pos! <= Math.max(x.to, x.from + 1) && x.fixes.length);
      for (const x of hits.slice(0, 3)) {
        out.push({ label: x.fixes[0].label, icon: x.sev === 'E' ? 'error' : 'warn', hint: x.msg, onClick: (cc: Ctx) => { if (cc.editor) applyFix(cc.editor, x); } });
      }
      if (out.length) out.push({ separator: true });
    }
    const sh = t.nodes.find((n) => n.type === 'structHeading');
    if (sh) {
      return [...out, { label: 'Вид элемента', icon: 'bookmark', children: STRUCT_ORDER.map((k) => ({
        label: STRUCT_KINDS[k], checked: sh.attrs.kind === k,
        onClick: (cc: Ctx) => cc.editor?.chain().command(({ tr }) => { tr.setNodeMarkup(sh.pos, undefined, { ...sh.attrs, kind: k }); return true; }).run(),
      })) }];
    }
    out.push({ command: 'refs.xref' }, { command: 'refs.cite' }, { command: 'refs.footnote' });
    const app = t.nodes.find((n) => n.type === 'appendix');
    if (app) {
      out.push({ label: 'Статус приложения', icon: 'attach', children: ['обязательное', 'рекомендуемое', 'справочное'].map((s) => ({
        label: s, checked: app.attrs.status === s,
        onClick: (cc: Ctx) => cc.editor?.chain().command(({ tr }) => { tr.setNodeMarkup(app.pos, undefined, { ...app.attrs, status: s }); return true; }).run(),
      })) });
    }
    return out;
  } });

  registerLint(r);
  // строки ГОСТ для источников - после открытия проекта и изменения библиотеки/порядка
  const fmt = () => scheduleFormat(ctx);
  r.events.on('project:opened', (p: any) => {
    fmt();
    void applyProfileToNew(p?.report);
  });
  appStore.subscribe((s, p) => { if (s.projectId && (s.sources !== p.sources || s.meta?.options !== p.meta?.options)) fmt(); });
  r.events.on('editor:created', (ed: any) => setTimeout(() => migrateDefs(ed), 0));
}

/** Проекты до перехода на СТО 2026 (3.5.1): «Термины и определения» / «Перечень сокращений» -> один элемент
 *  «Определения, обозначения и сокращения» (второй такой заголовок убирается - текст под ним остаётся). */
function migrateDefs(ed: any) {
  if (!ed || ed.isDestroyed) return;
  const tr = ed.state.tr;
  let seen = false;
  let n = 0;
  const hits: { pos: number; node: any }[] = [];
  ed.state.doc.forEach((node: any, pos: number) => { if (node.type.name === 'structHeading' && ['defs', 'terms', 'abbrev'].includes(node.attrs.kind)) hits.push({ pos, node }); });
  for (let i = hits.length - 1; i >= 0; i--) {
    const h = hits[i];
    const isFirst = i === 0;
    if (isFirst) {
      if (h.node.attrs.kind !== 'defs') { tr.setNodeMarkup(h.pos, undefined, { ...h.node.attrs, kind: 'defs' }); n++; }
      seen = true;
    } else { tr.delete(h.pos, h.pos + h.node.nodeSize); n++; }
  }
  if (!n || !seen) return;
  tr.setMeta('addToHistory', false);
  ed.view.dispatch(tr);
  ctx.toast('«Термины и определения» и «Перечень сокращений» объединены в «Определения, обозначения и сокращения» (СТО 2026, 3.5.1)', 'info', 8000);
}

/** Новый проект (ещё не сохранялся): данные студента - из профиля, руководитель - последний запомненный. */
async function applyProfileToNew(report: any) {
  if (!report || report.revision !== 0) return;
  try {
    const prof = await loadProfile();
    const m = appStore.getState().meta;
    if (!m || !prof.saved) return;
    const st = { ...m.student, ...prof.student };
    const changed = JSON.stringify(st) !== JSON.stringify(m.student);
    const teacher = !m.teacher.name && prof.teachers[0] ? prof.teachers[0] : null;
    if (changed || teacher) appStore.getState().setMeta((x) => ({ ...x, student: st, ...(teacher ? { teacher: { ...teacher } } : {}) }));
  } catch { /* профиль недоступен - остаются данные по умолчанию */ }
}
